import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";

const MOMENTS = [
  {
    number: "01",
    labelEn: "THE CAMPUS",
    labelEs: "LA UNIVERSIDAD",
    en: "The UPR campus is woven into the neighborhood's daily life.",
    es: "La UPR forma parte de la vida cotidiana del barrio.",
  },
  {
    number: "02",
    labelEn: "THE STREETS",
    labelEs: "LAS CALLES",
    en: "Paseo de Diego, the market, cafés and independent shops.",
    es: "Paseo de Diego, el mercado, cafés y comercios locales.",
  },
  {
    number: "03",
    labelEn: "THE SCENE",
    labelEs: "LA ESCENA",
    en: "Live music, local stages and places to gather after dark.",
    es: "Música en vivo, escenarios locales y lugares de encuentro.",
  },
];

/**
 * A present-day introduction to Río Piedras before any history content.
 * The guide already has a photographic hero, so the compact version avoids
 * repeating the oversized title or adding another promotional banner.
 */
export default function RioPiedrasToday({ compact = false }) {
  const { lang } = useLang();
  const es = lang === "es";

  return (
    <section
      id={compact ? "rio-today-guide" : "rio-today"}
      aria-label={es ? "Río Piedras hoy" : "Río Piedras today"}
      className={compact
        ? "mt-6 border-y border-[#d4d1ca] py-4 text-[#191917] sm:mt-8 sm:py-5"
        : "mx-4 border-t-2 border-[#191917] pt-4 text-[#191917] sm:mx-6 sm:pt-5 lg:mx-auto lg:max-w-[1180px] dark:border-white dark:text-white"}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-[760px] min-w-0">
          <p className="text-[10px] font-extrabold uppercase tracking-[.14em] text-[#b43e36] dark:text-[#ffaba1]">
            {es ? "EL BARRIO HOY / SAN JUAN" : "THE NEIGHBORHOOD TODAY / SAN JUAN"}
          </p>
          {!compact && (
            <h2 className="mt-2 text-[clamp(36px,8vw,70px)] font-extrabold uppercase leading-[.95] tracking-[-.057em]">
              RÍO PIEDRAS<span className="text-[#c6423a]">.</span>
            </h2>
          )}
          <p className={compact
            ? "mt-2 max-w-[70ch] text-[13px] leading-[1.65] text-[#615e58] sm:text-[14px]"
            : "mt-3 max-w-[64ch] text-[14px] font-medium leading-[1.7] text-[#66625c] dark:text-[#c2bcb1] sm:text-[16px]"}>
            {es
              ? "Más que un distrito universitario: un centro de comercio, cultura y vida de barrio. Sus mejores historias comienzan con los lugares y las personas de hoy."
              : "More than a university district: a center of commerce, culture and neighborhood life. Its story begins with the places and people here today."}
          </p>
        </div>
        {!compact && (
          <Link
            to="/RioPiedras"
            className="inline-flex min-h-10 shrink-0 items-center gap-1.5 border-b border-[#191917] text-[11px] font-bold transition hover:text-[#ad3d34] dark:border-white dark:text-white"
          >
            {es ? "Guía de Río Piedras" : "Explore the neighborhood"}
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}
      </div>
      <div className="mt-5 grid gap-0 border-t border-[#d8d4cc] sm:grid-cols-3 dark:border-white/20">
        {MOMENTS.map((moment, i) => (
          <div
            key={moment.number}
            className={`flex gap-3 border-b border-[#e6e2d9] py-3 last:border-b-0 dark:border-white/15 sm:gap-3.5 sm:border-b-0 sm:py-4 ${i > 0 ? "sm:border-l sm:border-[#d8d4cc] sm:pl-5 dark:sm:border-white/20" : "sm:pr-4"}`}
          >
            <span aria-hidden="true" className="pt-0.5 font-mono text-[10px] font-bold tabular-nums text-[#b43e36] dark:text-[#ffaba1]">
              {moment.number}
            </span>
            <div className="min-w-0">
              <h3 className="text-[10px] font-extrabold uppercase tracking-[.1em]">
                {es ? moment.labelEs : moment.labelEn}
              </h3>
              <p className="mt-1 max-w-[32ch] text-[12px] leading-[1.55] text-[#716d66] dark:text-[#c1bbb1]">
                {es ? moment.es : moment.en}
              </p>
            </div>
          </div>
        ))}
      </div>
      {!compact && (
        <a href="#rio-local-places" className="mt-2 inline-flex min-h-9 items-center gap-1 text-[11px] font-bold text-[#ad3d34] hover:underline dark:text-[#ffb3a8]">
          {es ? "Explorar lugares de hoy" : "Discover today's places"}
          <ArrowDownRight className="h-4 w-4" aria-hidden="true" />
        </a>
      )}
    </section>
  );
}
